import { Injectable } from '@angular/core';
import type {
  EditorIngredient,
  RecipeAttributes,
  NutritionSummary,
  TopMicro,
} from '../interfaces/recipe.dto';

/**
 * Pure calculation service for nutrition aggregation.
 * Optimized for cleaned BLS keys from Python NutrientTransformer.
 */
@Injectable({
  providedIn: 'root',
})
export class NutritionCalculatorService {
  // Key nutrients for macros (Clean names from Backend)
  private readonly PROTEIN_KEY = 'Protein';
  private readonly FAT_KEY = 'Fett';
  private readonly CARBS_KEY = 'Kohlenhydrate';
  private readonly FIBER_KEY = 'Ballaststoffe';

  /**
   * Micronutrient definitions for ranking.
   * Keys match Python NutrientTransformer output after _extract_nutrient_name.
   */
  private readonly MICRONUTRIENT_DEFINITIONS: {
    name: string;
    group: 'vitamin' | 'mineral';
    toMg: number;
    keys: string[];
  }[] = [
    // --- VITAMINE ---
    {
      name: 'Vitamin A',
      group: 'vitamin',
      toMg: 0.001,
      keys: ['Vitamin_A_RAE', 'Vitamin_A_RE', 'Vitamin A_RE', 'Vitamin_A'],
    },
    {
      name: 'Retinol',
      group: 'vitamin',
      toMg: 0.001,
      keys: ['Retinol'],
    },
    {
      name: 'Beta-Carotin',
      group: 'vitamin',
      toMg: 0.001,
      keys: ['Beta_Carotin'],
    },
    {
      name: 'Carotinoide',
      group: 'vitamin',
      toMg: 0.001,
      keys: ['Carotinoide_außer_CARTB'],
    },
    { name: 'Vitamin D', group: 'vitamin', toMg: 0.001, keys: ['Vitamin_D'] },
    {
      name: 'Vitamin D3',
      group: 'vitamin',
      toMg: 0.001,
      keys: ['Vitamin_D3'],
    },
    {
      name: 'Vitamin D2',
      group: 'vitamin',
      toMg: 0.001,
      keys: ['Vitamin_D2'],
    },
    { name: 'Vitamin E', group: 'vitamin', toMg: 1, keys: ['Vitamin_E'] },
    {
      name: 'Alpha-Tocopherol',
      group: 'vitamin',
      toMg: 1,
      keys: ['Alpha_Tocopherol'],
    },
    {
      name: 'Beta-Tocopherol',
      group: 'vitamin',
      toMg: 1,
      keys: ['Beta_Tocopherol'],
    },
    {
      name: 'Gamma-Tocopherol',
      group: 'vitamin',
      toMg: 1,
      keys: ['Gamma_Tocopherol'],
    },
    {
      name: 'Delta-Tocopherol',
      group: 'vitamin',
      toMg: 1,
      keys: ['Delta_Tocopherol'],
    },
    {
      name: 'Alpha-Tocotrienol',
      group: 'vitamin',
      toMg: 1,
      keys: ['Alpha_Tocotrienol'],
    },
    { name: 'Vitamin K', group: 'vitamin', toMg: 0.001, keys: ['Vitamin_K'] },
    {
      name: 'Vitamin K1',
      group: 'vitamin',
      toMg: 0.001,
      keys: ['Vitamin_K1'],
    },
    {
      name: 'Vitamin K2',
      group: 'vitamin',
      toMg: 0.001,
      keys: ['Vitamin_K2'],
    },
    { name: 'Vitamin B1', group: 'vitamin', toMg: 1, keys: ['Vitamin_B1'] },
    { name: 'Vitamin B2', group: 'vitamin', toMg: 1, keys: ['Vitamin_B2'] },
    {
      name: 'Niacin-Äquivalent',
      group: 'vitamin',
      toMg: 1,
      keys: ['Niacin_Äquivalent'],
    },
    { name: 'Niacin', group: 'vitamin', toMg: 1, keys: ['Niacin'] },
    {
      name: 'Pantothensäure',
      group: 'vitamin',
      toMg: 1,
      keys: ['Pantothensäure'],
    },
    {
      name: 'Vitamin B6',
      group: 'vitamin',
      toMg: 0.001,
      keys: ['Vitamin_B6'],
    },
    { name: 'Biotin', group: 'vitamin', toMg: 0.001, keys: ['Biotin'] },
    {
      name: 'Folat-Äquivalent',
      group: 'vitamin',
      toMg: 0.001,
      keys: ['Folat_Äquivalent'],
    },
    { name: 'Folat', group: 'vitamin', toMg: 0.001, keys: ['Folat'] },
    {
      name: 'Folsäure',
      group: 'vitamin',
      toMg: 0.001,
      keys: ['Folsäure_synthetisch'],
    },
    {
      name: 'Vitamin B12',
      group: 'vitamin',
      toMg: 0.001,
      keys: ['Vitamin_B12'],
    },
    { name: 'Vitamin C', group: 'vitamin', toMg: 1, keys: ['Vitamin_C'] },

    // --- MINERALSTOFFE & SPURENELEMENTE ---
    { name: 'Natrium', group: 'mineral', toMg: 1, keys: ['Natrium'] },
    { name: 'Chlorid', group: 'mineral', toMg: 1, keys: ['Chlorid'] },
    { name: 'Kalium', group: 'mineral', toMg: 1, keys: ['Kalium'] },
    { name: 'Calcium', group: 'mineral', toMg: 1, keys: ['Calcium'] },
    { name: 'Magnesium', group: 'mineral', toMg: 1, keys: ['Magnesium'] },
    { name: 'Phosphor', group: 'mineral', toMg: 1, keys: ['Phosphor'] },
    { name: 'Schwefel', group: 'mineral', toMg: 1, keys: ['Schwefel'] },
    { name: 'Eisen', group: 'mineral', toMg: 1, keys: ['Eisen'] },
    { name: 'Zink', group: 'mineral', toMg: 1, keys: ['Zink'] },
    { name: 'Iodid', group: 'mineral', toMg: 0.001, keys: ['Iodid'] },
    { name: 'Kupfer', group: 'mineral', toMg: 0.001, keys: ['Kupfer'] },
    { name: 'Mangan', group: 'mineral', toMg: 0.001, keys: ['Mangan'] },
    { name: 'Fluorid', group: 'mineral', toMg: 0.001, keys: ['Fluorid'] },
    { name: 'Chrom', group: 'mineral', toMg: 0.001, keys: ['Chrom'] },
    { name: 'Molybdän', group: 'mineral', toMg: 0.001, keys: ['Molybdän'] },
  ];

  /**
   * Calculate nutrition summary for all ingredients.
   */
  calculateSummary(
    ingredients: EditorIngredient[],
    portions: number,
  ): NutritionSummary {
    const totals = this.aggregateNutrients(ingredients);
    const portionDivisor = portions > 0 ? portions : 1;

    // Calculate energy
    let totalKcal = 0;
    let totalKj = 0;
    for (const ing of ingredients) {
      if (ing.calculated_energy && ing.grams != null) {
        totalKcal += ing.calculated_energy.kcal_per_gram * ing.grams;
        totalKj += ing.calculated_energy.kJ_per_gram * ing.grams;
      }
    }

    // Get macros
    const protein = this.findNutrientValue(totals, this.PROTEIN_KEY);
    const fat = this.findNutrientValue(totals, this.FAT_KEY);
    const carbs = this.findNutrientValue(totals, this.CARBS_KEY);
    const fiber = this.findNutrientValue(totals, this.FIBER_KEY);

    // Get top micronutrients
    const topMicros = this.getTopMicronutrients(totals, 10);

    // Calculate per-portion values
    const proteinPerPortion = protein / portionDivisor;
    const fatPerPortion = fat / portionDivisor;
    const carbsPerPortion = carbs / portionDivisor;

    // Calculate macro calories and percentages
    // Protein: 4 kcal/g, Fat: 9 kcal/g, Carbs: 4 kcal/g
    const proteinKcal = proteinPerPortion * 4;
    const fatKcal = fatPerPortion * 9;
    const carbsKcal = carbsPerPortion * 4;
    const totalMacroKcal = proteinKcal + fatKcal + carbsKcal;

    const proteinPct =
      totalMacroKcal > 0 ? (proteinKcal / totalMacroKcal) * 100 : 0;
    const fatPct = totalMacroKcal > 0 ? (fatKcal / totalMacroKcal) * 100 : 0;
    const carbsPct =
      totalMacroKcal > 0 ? (carbsKcal / totalMacroKcal) * 100 : 0;

    return {
      kcal_per_portion: Math.round((totalKcal / portionDivisor) * 10) / 10,
      kj_per_portion: Math.round((totalKj / portionDivisor) * 10) / 10,
      protein_g: Math.round(proteinPerPortion * 10) / 10,
      fat_g: Math.round(fatPerPortion * 10) / 10,
      carbs_g: Math.round(carbsPerPortion * 10) / 10,
      fiber_g: Math.round((fiber / portionDivisor) * 10) / 10,
      protein_pct: Math.round(proteinPct),
      fat_pct: Math.round(fatPct),
      carbs_pct: Math.round(carbsPct),
      top_micros: topMicros.map((m) => ({
        ...m,
        value: Math.round((m.value / portionDivisor) * 100) / 100,
      })),
    };
  }

  /**
   * Calculate dietary attributes based on ingredient flags.
   */
  calculateAttributes(ingredients: EditorIngredient[]): RecipeAttributes {
    let isVegan = true;
    let isVegetarian = true;
    let isGlutenfree = true;
    let isLactosefree = true;
    let isNutfree = true;
    let hasDairy = false;
    let hasMeat = false;
    let hasFish = false;
    let hasSeafood = false;
    let hasEggs = false;

    for (const ing of ingredients) {
      const meta = ing.metadata;
      if (!meta) continue;

      if (meta.is_vegan === false) isVegan = false;
      if (meta.is_meat === true || meta.is_fish === true) isVegetarian = false;
      if (meta.is_glutenfree === false) isGlutenfree = false;
      if (meta.is_lactosefree === false) isLactosefree = false;
      if (meta.is_nutfree === false) isNutfree = false;

      if (meta.is_dairy === true) hasDairy = true;
      if (meta.is_meat === true) hasMeat = true;
      if (meta.is_fish === true) hasFish = true;
      if (meta.is_seafood === true) hasSeafood = true;
      if (meta.is_egg === true) hasEggs = true;
    }

    return {
      is_vegan: isVegan,
      is_vegetarian: isVegetarian,
      is_glutenfree: isGlutenfree,
      is_lactosefree: isLactosefree,
      is_nutfree: isNutfree,
      has_dairy: hasDairy,
      has_meat: hasMeat,
      has_fish: hasFish,
      has_seafood: hasSeafood,
      has_eggs: hasEggs,
    };
  }

  /**
   * Calculate grams for an ingredient based on unit and quantity.
   */
  calculateGrams(
    quantity: number,
    unitId: number,
    availableUnits: { unit_id: number; conversion_to_grams: number }[],
  ): number {
    const unit = availableUnits.find((u) => u.unit_id === unitId);
    if (!unit) return quantity;
    return quantity * unit.conversion_to_grams;
  }

  /**
   * Aggregate all nutrient values from ingredients.
   */
  private aggregateNutrients(
    ingredients: EditorIngredient[],
  ): Record<string, number> {
    const totals: Record<string, number> = {};

    for (const ing of ingredients) {
      if (!ing.matrix || ing.grams == null) continue;

      for (const [key, valuePerGram] of Object.entries(ing.matrix)) {
        if (valuePerGram == null) continue;

        const totalValue = valuePerGram * ing.grams;
        totals[key] = (totals[key] ?? 0) + totalValue;
      }
    }

    return totals;
  }

  /**
   * Find nutrient value by direct key access.
   */
  private findNutrientValue(
    totals: Record<string, number>,
    key: string,
  ): number {
    return totals[key] ?? 0;
  }

  /**
   * Get top N micronutrients by absolute weight in mg.
   */
  private getTopMicronutrients(
    totals: Record<string, number>,
    count: number,
  ): TopMicro[] {
    const vitaminTarget = Math.floor(count / 2);
    const mineralTarget = count - vitaminTarget;
    const vitamins: TopMicro[] = [];
    const minerals: TopMicro[] = [];

    for (const nutrient of this.MICRONUTRIENT_DEFINITIONS) {
      const rawValue = this.findFirstNutrientValue(totals, nutrient.keys);
      const valueInMg = rawValue * nutrient.toMg;
      if (valueInMg > 0) {
        const row: TopMicro = {
          name: nutrient.name,
          value: valueInMg,
          unit: 'mg',
        };
        if (nutrient.group === 'vitamin') {
          vitamins.push(row);
        } else {
          minerals.push(row);
        }
      }
    }

    const topVitamins = vitamins
      .sort((a, b) => b.value - a.value)
      .slice(0, vitaminTarget);
    const topMinerals = minerals
      .sort((a, b) => b.value - a.value)
      .slice(0, mineralTarget);

    const selected = [...topVitamins, ...topMinerals];
    if (selected.length >= count) {
      return selected.sort((a, b) => b.value - a.value).slice(0, count);
    }

    // Backfill if one group has fewer candidates than requested.
    const selectedNames = new Set(selected.map((row) => row.name));
    const remaining = [...vitamins, ...minerals]
      .filter((row) => !selectedNames.has(row.name))
      .sort((a, b) => b.value - a.value);

    return [...selected, ...remaining]
      .sort((a, b) => b.value - a.value)
      .slice(0, count);
  }

  private findFirstNutrientValue(
    totals: Record<string, number>,
    keys: string[],
  ): number {
    for (const key of keys) {
      const value = this.findNutrientValue(totals, key);
      if (value > 0) return value;
    }
    return 0;
  }
}
